import type { z } from "zod";
import type { financialAccountWithIntegrationAccountsResponseSchema } from "../schemas/financialAccount.schema";
import { Subaccount, SubaccountBase } from "@common/types/subaccount";
import { Sortable } from "./base/sortable";
import { Entity, EntityBase } from "./entity";
import {
    IntegrationAccount,
    IntegrationAccountBase,
} from "./integrationAccount";

export enum FinancialAccountType {
    CHECKING = "checking",
    SAVINGS = "savings",
    CREDIT_CARD = "credit_card",
    LOAN = "loan",
    LINE_OF_CREDIT = "line_of_credit",
    INVESTMENT = "investment",
    PAYMENT_PROCESSOR = "payment_processor",
    CASH = "cash",
    OTHER = "other",
}

/**
 * Base type for FinancialAccount - allows undefined on relations.
 * Used by entity classes (TypeORM).
 */
export interface FinancialAccountBase extends Sortable {
    id: number;
    name: string | null;
    isBusiness: boolean | null;
    balance: number | null;
    balanceSyncedAt: Date | null;
    transactionsSyncedAt: Date | null;
    continuousReconciliationEnabledAt: Date | null;
    createdAt: Date | null;
    entityId: number | null;
    entity?: EntityBase | null;
    sourceType: FinancialAccountSourceType;
    routingNumber: string | null;
    wireRoutingNumber: string | null;
    accountNumberMask: string | null;
    disabled: boolean;
    financialType: FinancialAccountType;
    integrationAccounts?: IntegrationAccountBase[];
    institutionName: string | null;
    subaccounts?: SubaccountBase[];
    bookkeepingStartDate: string | null;
    bookkeepingEndDate: string | null;
}

/**
 * Strict type for FinancialAccount - uses strict nested types.
 * Used by DTOs and service boundaries after relations are loaded.
 */
export interface FinancialAccount extends Omit<
    FinancialAccountBase,
    "entity" | "integrationAccounts" | "subaccounts"
> {
    entity: Entity | null;
    integrationAccounts?: IntegrationAccount[];
    subaccounts?: Subaccount[];
}

/**
 * FinancialAccount with loaded entity and integration accounts, derived
 * from the list-endpoint response schema.
 */
export type FinancialAccountWithIntegrationAccounts = z.infer<
    typeof financialAccountWithIntegrationAccountsResponseSchema
>;

export enum FinancialAccountSourceType {
    STRIPE = "stripe",
    GUSTO = "gusto",
    ADP_RUN = "adp_run",
    FILE_IMPORT = "file_import",
    PLAID = "plaid",
    MERCURY = "mercury",
    RAMP = "ramp",
    PAYPAL = "paypal",
    MANUAL = "manual",
    BILLCOM = "billcom",
    CHECK = "check",
    DEPOSIT = "deposit",
}

/**
 * Source types that cannot pull transactions on their own (no integration
 * puller). An integration account mounted on such a financial account is the
 * only live transaction source, so provider sync must not skip it.
 */
export const PASSIVE_FINANCIAL_ACCOUNT_SOURCE_TYPES: readonly FinancialAccountSourceType[] =
    [
        FinancialAccountSourceType.FILE_IMPORT,
        FinancialAccountSourceType.MANUAL,
        FinancialAccountSourceType.CHECK,
        FinancialAccountSourceType.DEPOSIT,
    ];

export const CASH_ITEM_SOURCE_TYPES: FinancialAccountSourceType[] = [
    FinancialAccountSourceType.CHECK,
    FinancialAccountSourceType.DEPOSIT,
];
